import { apiSlice } from './apiSlice'

export type ReportsDashboard = {
  total_cases: number
  total_teams: number
  total_courtrooms: number
  submitted_scores_count: number
  average_total_score: number | null
  leaderboard: Array<{
    team_id: string
    team_name: string
    avg_score: number
    score_count: number
  }>
}

export type RoundCompletion = {
  case_id: string
  round_id: string
  expected_total: number
  submitted_count: number
  complete: boolean
  judges: Array<{
    judge_id: string
    judge_name: string
    judge_email?: string | null
    done: number
    expected: number
    complete: boolean
  }>
  teams: Array<{
    team_id: string
    team_name: string
    university?: string | null
  }>
}

export type VideoTiebreakBreakdown = {
  creativity: number
  presentation: number
  professionalism: number
}

export type RankingJudgeDetail = {
  judge_id: string
  judge_name: string
  judge_total: number
  submissions: Array<{
    submission_type: string  // 'memorial' | 'evidence' | 'transcript' | ''
    score: number
    score_id?: string | null
  }>
}

export type SubmissionJudgeBreakdown = {
  judge_id: string
  judge_name: string
  memorial_score: number
  evidence_score: number
  total: number
}

export type RankingRow = {
  rank: number
  team_id: string
  team_name: string
  university?: string | null
  judge_count: number
  score_count?: number  // tổng số phiếu (memorial + evidence + ...) tích lũy
  // ── Spec docs/operations/Dac_ta_Cham_diem_So_ket_video.md ──
  main_score?: number             // Điểm bài thi chính TB (Điều 10.2, max 100; R1 dual: max 200)
  main_score_total?: number       // Sum (chưa /num GK) — debug only
  main_score_max?: number         // 100 (legacy) | 200 (R1 dual)
  video_avg_score?: number        // Điểm video GK chấm TB (Điều 10.3, max 30)
  video_judge_count?: number      // số GK đã chấm video
  video_source?: 'judge_avg' | 'btc_manual' | 'none'
  effective_video_score?: number  // điểm video dùng để tie-break (= judge_avg HOẶC BTC fallback)
  // ── Spec PHẦN 6 — R1 dual_claimant_packets ──
  submission_1_score?: number
  submission_1_judges?: SubmissionJudgeBreakdown[]
  submission_1_judge_count?: number
  submission_1_representing_role?: string | null
  submission_2_score?: number
  submission_2_judges?: SubmissionJudgeBreakdown[]
  submission_2_judge_count?: number
  submission_2_representing_role?: string | null
  // ── 2026-05-25: Late-penalty Bảng phân vai cho R2/R3 ──
  // BE trừ -20đ vào main_score / total_score nếu team nộp role sheet muộn.
  raw_main_score?: number          // điểm chưa trừ penalty (FE hiển thị breakdown)
  raw_main_score_total?: number    // tổng chưa trừ penalty
  late_penalty_points?: number     // 0 hoặc 20 (chỉ set cho R2/R3)
  late_penalty_detail?: {
    is_late?: boolean | null
    minutes_late?: number | null
    penalty_points?: number
    sheet_id?: string | null
    submitted?: boolean
  }
  // ── Legacy (giữ backward-compat) ──
  average_score: number  // = main_score (ĐÃ trừ penalty với R2/R3)
  total_score: number    // = main_score_total (R1 dual: = main_score)
  judge_details?: RankingJudgeDetail[]  // breakdown per-judge (legacy single)
  video_tiebreak_total?: number  // BTC chấm tay (legacy)
  video_tiebreak?: VideoTiebreakBreakdown  // BTC chấm tay (legacy)
  tied_with_neighbor?: boolean
  // S-1 fix (audit 2026-05-22): true khi chưa đủ phiếu (sub1/sub2 thiếu GK).
  incomplete?: boolean
  submission_1_incomplete?: boolean
  submission_2_incomplete?: boolean
}

export type RankingResult = {
  case_id: string
  round_id: string | null
  total_teams: number
  ranking: RankingRow[]
  tie_break_applied?: boolean
  flow?: 'dual_claimant_packets' | 'legacy_single_role'  // PHẦN 6 — chỉ trả khi R1 dual
  main_score_max?: number  // 200 với dual; undefined với legacy (UI auto-fallback 100)
  video_tiebreak_max?: number  // 30 với dual
}

export type NextRoundSchedule = {
  bracket_at?: string
  role_sheet_due?: string
  hearing_at?: string
}

export type PublishRoundBody = {
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  title?: string
  note?: string
  next_round_label?: string
  next_round_schedule?: NextRoundSchedule
  force?: boolean
}

export type PublishRoundResult = {
  published_id: string
  case_id: string
  round_id: string
  advancing_team_ids: string[]
  notified_teams: number
  ranking: RankingRow[]
  idempotent?: boolean  // BE trả về true nếu là duplicate call trong 5 phút
}

// Phase 6 (2026-05-25): per-match publish — Vòng Bán kết publish từng cặp.
export type PublishMatchBody = {
  case_id: string
  round_id: string
  match_id: string
  winner_team_id: string
  title?: string
  note?: string
  force?: boolean
}

export type PublishMatchResult = {
  published_id: string
  case_id: string
  round_id: string
  match_id: string
  winner_team_id: string
  loser_team_id?: string | null
  notified_teams?: number
  idempotent?: boolean
  // Auto-trigger transition Bán kết → Chung kết. None = chưa đủ điều kiện
  // (mới 1/2 match publish); dict = đã transition thành công.
  auto_advanced_to_final?: {
    final_round_id: string
    finalists: string[]
    final_bracket_id?: string
    idempotent?: boolean
  } | null
}

export type AdvanceToFinalBody = {
  case_id: string
  force?: boolean
}

export const reportsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // C2 (audit 2026-05-22): providesTags['Score'] để Reports tự invalidate
    // khi scoring mutation (create/update/submit/unlock/reopen) fire → admin
    // không cần reload trang để thấy điểm mới.
    getReportsDashboard: builder.query<ReportsDashboard, void>({
      query: () => '/reports/dashboard',
      providesTags: ['Score'],
    }),
    exportScoresCsv: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/scores.csv',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    exportSummaryTxt: builder.query<string, void>({
      query: () => ({
        url: '/reports/export/summary.txt',
        responseHandler: (response: Response) => response.text(),
      }),
    }),
    exportSummaryPdf: builder.query<Blob, void>({
      query: () => ({
        url: '/reports/export/summary.pdf',
        // YC6 (audit 2026-05-23): khi BE trả lỗi (vd 503 no_report_font, 400),
        // RTK default vẫn coi 4xx/5xx là success nếu responseHandler không
        // check response.ok → frontend hiển thị blob lỗi không readable.
        // Pattern này (copy từ downloadDocument): parse JSON detail khi !ok.
        responseHandler: async (response: Response) => {
          if (!response.ok) {
            const contentType = response.headers.get('content-type') || ''
            if (contentType.includes('application/json')) {
              try {
                const j = await response.json()
                throw j
              } catch (e) {
                throw e
              }
            }
            const text = await response.text().catch(() => '')
            throw { detail: text || `PDF export failed (HTTP ${response.status})` }
          }
          return response.blob()
        },
      }),
    }),
    // UAT-TC #15: kiểm tra giám khảo đã chấm xong
    getRoundCompletion: builder.query<RoundCompletion, { case_id: string; round_id: string }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/round-completion',
        params: { case_id, round_id },
      }),
      providesTags: ['Score'],
    }),
    // UAT-TC #15 + Chung kết #16: ranking theo vòng (round_id=null → tổng hợp)
    getRanking: builder.query<RankingResult, { case_id: string; round_id?: string | null }>({
      query: ({ case_id, round_id }) => ({
        url: '/reports/ranking',
        params: round_id ? { case_id, round_id } : { case_id },
      }),
      providesTags: ['Score'],
    }),
    // Bug fix 2026-05-25: lấy danh sách round đã publish (durable marker từ
    // collection `published_results`). FE dùng thay cho việc suy từ
    // team.competition_status (false-positive khi status set bởi flow khác).
    getPublishedRounds: builder.query<
      {
        case_id: string
        published_round_ids: string[]
        details: Array<{ round_id: string; published_at: string; published_id?: string }>
      },
      string
    >({
      query: (case_id) => ({
        url: '/reports/published-rounds',
        params: { case_id },
      }),
      providesTags: (_r, _e, case_id) => [{ type: 'Score', id: `pub-${case_id}` }],
    }),
    // UAT-TC #16 + Bán kết #11 + Chung kết #18: công bố kết quả
    publishRound: builder.mutation<PublishRoundResult, PublishRoundBody>({
      query: (body) => ({
        url: '/reports/publish-round',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Score', 'Team'],
    }),
    // Phase 6 (2026-05-25): per-match publish — Vòng Bán kết. Auto-trigger
    // advance to final khi cả 2 match đã publish.
    publishMatch: builder.mutation<PublishMatchResult, PublishMatchBody>({
      query: (body) => ({
        url: '/reports/publish-match',
        method: 'POST',
        body,
      }),
      // 2026-05-26: thêm Case + Round + Bracket — BE auto-trigger advance_to_final
      // sau publish-match #2 sẽ update case.current_round_id + tạo final bracket.
      // FE CasesPage / BracketsPage / AchievementsPage cần re-fetch để hiển thị
      // round mới + bracket CK mới + tiến trình toàn giải.
      invalidatesTags: ['Score', 'Team', 'Case', 'Round', 'Bracket'],
    }),
    // Phase 6 (2026-05-25): manual fallback nếu auto-trigger fail. Idempotent.
    advanceToFinal: builder.mutation<
      {
        final_round_id?: string
        finalists?: string[]
        final_bracket_id?: string
        idempotent?: boolean
        note?: string  // "pending" nếu chưa đủ điều kiện + force=false
      },
      AdvanceToFinalBody
    >({
      query: (body) => ({
        url: '/reports/advance-to-final',
        method: 'POST',
        body,
      }),
      // 2026-05-26: thêm Case+Round+Bracket — switch current_round_id sang
      // FINAL, tạo final bracket → FE CasesPage / BracketsPage / Achievements
      // / HomePage cần re-fetch ngay.
      invalidatesTags: ['Score', 'Team', 'Case', 'Round', 'Bracket'],
    }),
    // C3-a (audit 2026-05-22): R1 shortlist — admin bulk set team status.
    setR1Shortlist: builder.mutation<
      {
        case_id: string
        shortlisted_count: number
        eliminated_count: number
        shortlisted_team_ids: string[]
      },
      { case_id: string; shortlisted_team_ids: string[] }
    >({
      query: (body) => ({
        url: '/reports/r1-shortlist',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Team', 'Score'],
    }),
    // Tie-break Điều 10.3 — chấm Video clip
    setVideoTiebreak: builder.mutation<
      any,
      { team_id: string; creativity: number; presentation: number; professionalism: number }
    >({
      query: ({ team_id, ...body }) => ({
        url: `/teams/${team_id}/video-tiebreak`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (res, err, { team_id }) => [
        { type: 'Team', id: team_id },
        'Team',
      ],
    }),
    // Public Guest endpoints
    getPublicResultsCases: builder.query<
      Array<{ case_id: string; title: string; case_type?: string | null }>,
      void
    >({
      query: () => '/public/results/cases',
    }),
    getPublicLatestResults: builder.query<
      {
        case_id: string
        case_title: string
        round: { round_name: string; round_type: string } | null
        published_at: string
        title: string
        note: string
        total_teams: number
        advancing_count: number
        advancing_teams: Array<{
          team_name: string
          university: string
          competition_status: string | null
        }>
      },
      string
    >({
      query: (caseId) => `/public/results/${caseId}/latest`,
    }),
    getPublicVerdicts: builder.query<
      Array<{
        courtroom_id: string
        round_name: string | null
        round_type: string | null
        verdict_summary: string | null
        verdict_legal_basis: string | null
        verdict_penalty: string | null
        winner_team: { team_id: string; team_name: string } | null
        published_at: string | null
      }>,
      string
    >({
      query: (caseId) => `/public/verdicts/${caseId}`,
    }),
    getPublicSchedule: builder.query<
      {
        case_id: string
        case_title: string
        rounds: Array<{
          round_id: string
          round_name: string
          round_type: string
          scheduled_date: string | null
          courtroom_started_at: string | null
          matches: Array<{
            team_plaintiff: { name: string; university: string } | null
            team_defendant: { name: string; university: string } | null
            // P0+++ RBAC audit 2026-05-20: bỏ courtroom_id khỏi public schedule
            // (chặn enum chain → VOD leak). UI dùng livestream_enabled để show
            // nút "Xem trực tiếp" nếu cần.
            livestream_enabled: boolean
            started_at: string | null
          }>
        }>
      },
      string
    >({
      query: (caseId) => `/public/schedule/${caseId}`,
    }),
  }),
})

export const {
  useGetReportsDashboardQuery,
  useLazyExportScoresCsvQuery,
  useLazyExportSummaryTxtQuery,
  useLazyExportSummaryPdfQuery,
  useGetRoundCompletionQuery,
  useLazyGetRoundCompletionQuery,
  useGetRankingQuery,
  useLazyGetRankingQuery,
  useGetPublishedRoundsQuery,
  usePublishRoundMutation,
  usePublishMatchMutation,
  useAdvanceToFinalMutation,
  useSetR1ShortlistMutation,
  useSetVideoTiebreakMutation,
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicVerdictsQuery,
  useGetPublicScheduleQuery,
} = reportsApi
